/* eslint-disable */
import {Injectable} from '@angular/core';
import {EmptyMessage,} from '../util/messages';
import {Observable} from 'rxjs';
import {FormResource, FormResourcePathGenerator} from '../form/form-resource.service';
import {HttpClient} from '@angular/common/http';
import {BaseHttpService, ResourceHelper} from '../util/http-services';
import {ResourceQueryResult} from '../util/services';
import {DqlStoredQueryResourceService} from '../dql/dql-stored-query-resource.service';
import {DqlResourceModel} from '../dql/dql-resource.model';

/* eslint-enable */

@Injectable()
export class MasterDataResourceService extends BaseHttpService {

  private static readonly baseUrl: string = '/master-data/';

  private readonly _dqlStoredResourceService: DqlStoredQueryResourceService;

  // <editor-fold desc="CRUD">

  query(request: MasterDataResource.QueryRequest): Observable<ResourceQueryResult<MasterDataResource.MasterData>> {
    return this.http.get<ResourceQueryResult<MasterDataResource.MasterData>>(this.url, this.parseParams(request));
  }

  get(request: MasterDataResource.GetRequest): Observable<MasterDataResource.MasterData> {
    return this.http.get<MasterDataResource.MasterData>(this.url + `${request.master_data_id}`, this.parseParams(request));
  }

  create(request: MasterDataResource.CreateRequest): Observable<MasterDataResource.MasterData> {
    return this.http.post<MasterDataResource.MasterData>(this.url, request);
  }

  update(request: MasterDataResource.UpdateRequest): Observable<MasterDataResource.MasterData> {
    return this.http.put<MasterDataResource.MasterData>(this.url + `${request.master_data_id}`, request);
  }

  setDisabled(request: MasterDataResource.DisableRequest): Observable<EmptyMessage> {
    return this.http.patch<MasterDataResource.MasterData>(this.url + `${request.master_data_id}/disabled`, request);
  }

  // </editor-fold>

  // <editor-fold desc="Form">

  getForm(request: FormResource.GetFormRequest): Observable<FormResource.Form> {
    return this.http.get<FormResource.Form>(this.url + FormResourcePathGenerator.GetForm(request));
  }

  createFormGroup(request: FormResource.CreateGroupRequest): Observable<FormResource.CreateGroupResponse> {
    return this.http.post<FormResource.CreateGroupResponse>(this.url + FormResourcePathGenerator.CreateGroup(request), request);
  }

  updateFormGroup(request: FormResource.UpdateGroupRequest): Observable<FormResource.UpdateGroupResponse> {
    return this.http.put<FormResource.UpdateGroupResponse>(this.url + FormResourcePathGenerator.UpdateGroup(request), request);
  }

  disableFormGroup(request: FormResource.DisableGroupRequest): Observable<FormResource.DisableGroupResponse> {
    return this.http.patch<FormResource.DisableGroupResponse>(this.url + FormResourcePathGenerator.DisableGroup(request), request);
  }

  moveFormGroup(request: FormResource.MoveGroupRequest): Observable<FormResource.MoveGroupResponse> {
    return this.http.post<FormResource.MoveGroupResponse>(this.url + FormResourcePathGenerator.MoveGroup(request), request);
  }

  createFormField(request: FormResource.CreateFieldRequest): Observable<FormResource.CreateFieldResponse> {
    return this.http.post<FormResource.CreateFieldResponse>(this.url + FormResourcePathGenerator.CreateField(request), request);
  }

  updateFormField(request: FormResource.UpdateFieldRequest): Observable<FormResource.UpdateFieldResponse> {
    return this.http.post<FormResource.UpdateFieldResponse>(this.url + FormResourcePathGenerator.UpdateField(request), request);
  }

  disableFormField(request: FormResource.DisableFieldRequest): Observable<FormResource.DisableFieldResponse> {
    return this.http.patch<FormResource.DisableFieldResponse>(this.url + FormResourcePathGenerator.DisableField(request), request);
  }

  moveFormFieldToGroup(request: FormResource.MoveFieldToGroupRequest): Observable<FormResource.MoveFieldToGroupResponse> {
    return this.http.post<FormResource.MoveFieldToGroupResponse>(this.url + FormResourcePathGenerator.MoveFieldToGroup(request), request);
  }

  import(request: FormResource.ImportRequest): Observable<FormResource.Form> {
    return this.http.post<FormResource.Form>(this.url + FormResourcePathGenerator.Import(request), request);
  }

  export(request: FormResource.ExportRequest): Observable<FormResource.FormImportDocument> {
    return this.http.post<FormResource.FormImportDocument>(this.url + FormResourcePathGenerator.Export(request), null);
  }

  counts(request: EmptyMessage): Observable<MasterDataResource.MasterDataCount[]> {
    return this.http.get<MasterDataResource.MasterDataCount[]>(this.url + `counts`, this.parseParams(request));
  }

  getDqlModel(request: MasterDataResource.DqlRequest): Observable<DqlResourceModel.QueryableModel> {
    if (request.master_data_id) {
      return this.http.get<DqlResourceModel.QueryableModel>(this.url + `dql/model/${request.master_data_id}` + '?no_progress_bar=true');
    } else {
      return this.http.get<DqlResourceModel.QueryableModel>(this.url + `dql/model` + '?no_progress_bar=true');
    }
  }

  // </editor-fold>

  constructor(private http: HttpClient, resourceHelper: ResourceHelper) {
    super(resourceHelper, MasterDataResourceService.baseUrl);
    this._dqlStoredResourceService = new DqlStoredQueryResourceService(http, resourceHelper, MasterDataResourceService.baseUrl);
  }

  get dqlStoredResourceService(): DqlStoredQueryResourceService {
    return this._dqlStoredResourceService;
  }

}

export namespace MasterDataResource {

  // <editor-fold desc="CRUD">

  export interface MasterData {
    id: number;
    disabled: boolean;
    creation_time: string;
    update_time: string;
    name: string;
    description?: string;
    external_id?: string;
    client_required_id_field: string;
    form?: FormResource.Form;
    mobile_owner_type: string;
    owner_user_group_ids?: number[];
  }

  export interface QueryRequest {
    id?: string;
    disabled?: boolean;
    name?: string;
    external_id?: string;
    q?: string;
    fields?: string;
    order?: string;
    page_number?: number;
    number_of_items?: number;
    no_progress_bar?: boolean;
  }

  export interface GetRequest {
    master_data_id: number;
    with_form?: boolean;
    fields?: string;
  }

  export interface CreateRequest {
    name: string;
    description?: string;
    external_id?: string;
    client_required_id_field: string;
    mobile_owner_type: string;
    owner_user_group_ids: number[];
  }

  export interface UpdateRequest {
    master_data_id: number;
    name: string;
    description?: string;
    external_id?: string;
    client_required_id_field: string;
    mobile_owner_type: string;
    owner_user_group_ids: number[];
  }

  export interface DisableRequest {
    master_data_id: number;
    disabled: boolean;
  }

  export interface MasterDataCount {
    master_data_id: number;
    master_data_name: string;
    master_data_record_count: number;
  }

  export interface DqlRequest {
    master_data_id?: number;
  }

  // </editor-fold>

}
